import {ChangeDetectionStrategy, Component, inject, OnDestroy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faCircleCheck, faCircleExclamation, faFile, faFolder, faSpinner} from '@fortawesome/pro-solid-svg-icons';
import {BicButtonComponent, BicPopupComponent} from '@buildinginformationcloud/bic-ui-components';
import {Subscription} from 'rxjs';
import {SourceLinkView} from '../../../../api';
import {SourceLinkSyncProgressEvent} from '../../../../services/sse/sse-event.model';
import {SourceLinkSyncService, SourceLinkSyncState} from '../../../../services/source-link/source-link-sync.service';

export interface SourceLinkSyncProgressDialogData {
  link: SourceLinkView;
}

/**
 * Fortschritt eines SharePoint-Abgleichs, im Aufbau wie das Upload-Popup (`files-upload-progress`): erst die
 * Ordnerstruktur, dann die Dateien mit Zähler, Balken und aktueller Datei, unten „Im Hintergrund weiterlaufen
 * lassen“ und „Weiter“.
 *
 * Anders als beim Upload gibt es keinen Abbruch: der Abgleich läuft auf dem Server, Schließen ändert an ihm nichts.
 * Den Stand liefert {@link SourceLinkSyncService}; öffnet man das Popup mitten im Lauf, steht er ab der nächsten
 * Meldung (höchstens eine Sekunde) da.
 */
@Component({
  selector: 'app-source-link-sync-progress-dialog',
  templateUrl: './source-link-sync-progress-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BicPopupComponent, BicButtonComponent, FaIconComponent]
})
export class SourceLinkSyncProgressDialogComponent implements OnDestroy {
  readonly data = inject<SourceLinkSyncProgressDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject<MatDialogRef<SourceLinkSyncProgressDialogComponent>>(MatDialogRef);
  private readonly syncService = inject(SourceLinkSyncService);

  state: SourceLinkSyncState | undefined;
  /** Sekunden ohne erste Meldung (nur für den Hinweistext, wie beim Upload). */
  waitingSeconds = 0;

  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircleExclamation = faCircleExclamation;
  protected readonly faFolder = faFolder;
  protected readonly faFile = faFile;
  protected readonly faSpinner = faSpinner;

  private readonly sub: Subscription;
  private readonly waitTimer: ReturnType<typeof setInterval>;

  constructor() {
    this.sub = this.syncService.state$(this.data.link.id!).subscribe(state => this.state = state);
    this.waitTimer = setInterval(() => {
      if (!this.progress && !this.finished) {
        this.waitingSeconds++;
      }
    }, 1000);
  }

  get progress(): SourceLinkSyncProgressEvent | undefined {
    return this.state?.progress;
  }

  get finished(): boolean {
    return !!this.state?.finished;
  }

  /** Ohne Stand im Dienst (z. B. Abschlussmeldung schon vor dem Öffnen verarbeitet) zählt der Link, mit dem geöffnet wurde. */
  get result(): SourceLinkView {
    return this.state?.finished ?? this.data.link;
  }

  get source(): string {
    return `${this.data.link.siteName ?? 'SharePoint'} › ${this.data.link.folderPath}`;
  }

  /** Lesen der Quelle: Gesamtzahlen stehen noch nicht fest. */
  get listing(): boolean {
    return !this.finished && (!this.progress || this.progress.phase === 'LISTING');
  }

  get foldersPending(): boolean {
    return this.listing || this.progress?.phase === 'FOLDERS';
  }

  get showFolders(): boolean {
    return this.listing || (this.progress?.foldersTotal ?? 0) > 0;
  }

  get folderPercent(): number {
    const total = this.progress?.foldersTotal ?? 0;
    return total === 0 ? 0 : Math.round(((this.progress?.foldersDone ?? 0) / total) * 100);
  }

  get filesTotal(): number {
    return this.progress?.filesTotal ?? 0;
  }

  get filesDone(): number {
    if (this.finished && this.progress) {
      return this.progress.filesTotal;
    }
    return this.progress?.filesDone ?? 0;
  }

  get filePercent(): number {
    if (this.finished) {
      return 100;
    }
    return this.filesTotal === 0 ? 0 : Math.round((this.filesDone / this.filesTotal) * 100);
  }

  get finishing(): boolean {
    return !this.finished && this.progress?.phase === 'FINISHING';
  }

  get failedResult(): boolean {
    return this.result.status === SourceLinkView.StatusEnum.ERROR || this.result.status === SourceLinkView.StatusEnum.PAUSED;
  }

  get summary(): string {
    const p = this.progress;
    if (!p) {
      return `${this.result.fileCount} Dateien im Ordner`;
    }
    const parts = [`${p.transferred} übernommen`];
    if (p.updated) parts.push(`${p.updated} aktualisiert`);
    if (p.skipped) parts.push(`${p.skipped} übersprungen`);
    if (p.failed) parts.push(`${p.failed} fehlgeschlagen`);
    return parts.join(' · ');
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    clearInterval(this.waitTimer);
  }

  /** Dialog zu, der Abgleich läuft auf dem Server weiter; den Stand zeigt der Reiter am rechten Rand. */
  runInBackground(): void {
    this.dialogRef.close();
  }

  close(): void {
    if (this.finished) {
      this.syncService.dismiss(this.data.link.id!);
    }
    this.dialogRef.close();
  }
}
